import React, { useState } from 'react';
import PageWrapper from '../components/PageWrapper';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Satellite data for the cards - Image URLs set to your specified filenames!
const satelliteTypesData = [
  {
    id: 'natural-satellites',
    name: 'Natural Satellites (Moons)',
    shortDescription: 'Celestial bodies that orbit planets or smaller celestial bodies.',
    longDescription: 'Natural satellites, more commonly known as moons, are celestial bodies that orbit planets, dwarf planets, or even asteroids. They are gravitationally bound to their primary body. Our Earth has one natural satellite, the Moon, which plays a crucial role in stabilizing our planet\'s tilt and creating tides. Other planets in our solar system boast numerous moons, some of which, like Jupiter\'s Europa or Saturn\'s Titan, are larger than the planet Mercury and are considered potential candidates for harboring extraterrestrial life. Their formation varies, with some believed to be captured asteroids and others formed from debris coalescing after a planetary collision.',
    imageUrl: '18.png', // Example image: Earth's Moon
    examples: [
      { name: "Earth's Moon", image: '19.png' },
      { name: "Jupiter's Europa", image: '20.png' },
      { name: "Saturn's Titan", image: '21.png' }
    ],
    color: 'bg-indigo-700',
    borderColor: 'border-indigo-400/50'
  },
  {
    id: 'artificial-satellites',
    name: 'Artificial Satellites',
    shortDescription: 'Human-made objects launched into orbit for various purposes.',
    longDescription: 'Artificial satellites are technological marvels launched by humans into Earth\'s orbit, or even around other celestial bodies. They serve a myriad of purposes essential to modern life and scientific advancement. Communication satellites enable global phone calls, internet access, and television broadcasting. Navigation satellites, like those in the GPS constellation, provide precise location data worldwide. Weather satellites monitor atmospheric conditions and aid in forecasting. Scientific research satellites, such as the Hubble Space Telescope, observe the universe, while Earth observation satellites track climate change, natural disasters, and land use. These metallic marvels are powered by solar panels and communicate with ground stations to transmit valuable data.',
    imageUrl: '22.png', // Example image: Generic artificial satellite
    examples: [
      { name: "Hubble Space Telescope", image: '23.png' },
      { name: "International Space Station (ISS)", image: '24.png' },
      { name: "GPS Satellite", image: '25.png' }
    ],
    color: 'bg-teal-700',
    borderColor: 'border-teal-400/50'
  },
];

// Sub-component for a single satellite card
function SatelliteCard({ satellite, isExpanded, onToggleExpand }) {
  return (
    <motion.div
      layout
      className={`relative p-6 border rounded-lg shadow-xl ${satellite.borderColor} ${isExpanded ? 'col-span-full' : ''}`}
      transition={{ layout: { duration: 0.5, type: "spring", damping: 20, stiffness: 100 } }}
      style={{ overflow: 'hidden' }}
    >
      {/* Background Image Container */}
      <div
        className="absolute inset-0 transition-opacity duration-300 bg-center bg-cover rounded-lg opacity-60 group-hover:opacity-50"
        style={{ backgroundImage: `url(${satellite.imageUrl})` }}
      ></div>

      {/* Content Overlay - ensures text is readable over image */}
      <div className="relative z-10 flex flex-col h-full">
        <motion.h3
          layout="position"
          className="mb-3 text-2xl font-bold text-white-300"
        >
          {satellite.name}
        </motion.h3>
        <AnimatePresence mode="wait">
          {!isExpanded && (
            <motion.p
              key="short-desc"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="flex-grow text-white-400"
            >
              {satellite.shortDescription}
            </motion.p>
          )}
          {isExpanded && (
            <motion.div
              key="long-desc-content"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex-grow mt-4 text-white-400"
            >
              <p className="mb-4">{satellite.longDescription}</p>
              {satellite.examples && satellite.examples.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xl font-semibold text-white-200">Examples:</h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {satellite.examples.map((example, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <img
                          src={example.image}
                          alt={example.name}
                          className="object-cover mb-2 rounded-lg shadow-md opacity-100 w-60 h-60"
                        />
                        <p className="text-sm font-semibold text-center">{example.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <motion.button
          className="self-start px-4 py-2 mt-6 font-semibold text-white transition-colors duration-300 bg-blue-500 rounded-full hover:bg-blue-600"
          onClick={() => onToggleExpand(satellite.id)}
          layout="position"
        >
          {isExpanded ? 'View Less' : 'View More'}
        </motion.button>
      </div>
    </motion.div>
  );
}

export default function SatellitePage() {
  const [expandedCardId, setExpandedCardId] = useState(null);

  const handleToggleExpand = (id) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  return (
    <PageWrapper>
      <motion.div
        className="flex flex-col items-center justify-center min-h-screen p-8 text-white bg-gradient-to-br from-indigo-900 to-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.h1
          className="mb-6 text-6xl font-extrabold text-cyan-300 drop-shadow-lg"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 120, damping: 10 }}
        >
          Sentinels of Orbit: Satellites
        </motion.h1>

        <motion.p
          className="max-w-3xl mb-12 text-xl text-center text-white-300"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 100, damping: 10 }}
        >
          Satellites are objects that orbit a larger body. Explore the fascinating worlds of both natural and artificial satellites below!
        </motion.p>

        <motion.div
          layout
          className="grid w-full max-w-6xl grid-cols-1 gap-8 md:grid-cols-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          {satelliteTypesData.map((satellite) => (
            <SatelliteCard
              key={satellite.id}
              satellite={satellite}
              isExpanded={expandedCardId === satellite.id}
              onToggleExpand={handleToggleExpand}
            />
          ))}
        </motion.div>

        <Link to="/explore" className="px-8 py-3 mt-16 text-xl font-semibold text-white no-underline transition-all duration-300 ease-in-out bg-blue-600 rounded-full shadow-lg hover:bg-blue-700 hover:shadow-blue-500/50">
          Back to Explore
        </Link>
      </motion.div>
    </PageWrapper>
  );
}